import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface InboxApproval {
  /** assignment id (brukes mot useApproveTask/useRejectTask) */
  id: string;
  projectId: string;
  projectCode: string;
  projectName: string;
  customerName: string | null;
  taskDescription: string;
  phase: string | null;
  hours: number;
  fromUserId: string;
  fromUserName: string;
  fromUserAvatar: string | null;
  /** Dager siden assignment ble flyttet til 'completed'. Brukes til hast-indikator. */
  ageDays: number;
}

export interface InboxVoucher {
  id: string;
  projectId: string;
  projectCode: string;
  projectName: string;
  customerName: string | null;
  voucherNumber: string;
  supplierName: string | null;
  description: string;
  amount: number;
  voucherDate: string | null;
  dueDate: string | null;
  ageDays: number;
}

export interface InboxInvoice {
  projectId: string;
  projectCode: string;
  projectName: string;
  customerName: string | null;
  /** 'startup' = oppstartsfaktura mangler. 'final' = sluttfaktura mangler (prosjekt ferdig). */
  invoiceType: "startup" | "final";
  /** Andel av kontraktssum for denne fakturaen (oppstart = startup_invoice_pct, slutt = resten). */
  pct: number;
  /** Estimert beløp = totalsum × pct/100. */
  estimatedAmount: number;
  ageDays: number;
}

export interface PMInboxData {
  approvals: InboxApproval[];
  vouchers: InboxVoucher[];
  invoices: InboxInvoice[];
}

function daysSince(iso: string | null | undefined): number {
  if (!iso) return 0;
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return 0;
  return Math.max(0, Math.floor((Date.now() - d) / (1000 * 60 * 60 * 24)));
}

/**
 * PL-innboks: aggregerer alle handlinger som venter på brukeren.
 *
 * Filterregler matcher det eksisterende prosjektkortet bruker (verifisert
 * mot kilden, ikke gjettet):
 *
 * - **Godkjenninger**: assignments med `task_status='completed'` der
 *   linja er en isTaskLine (cost_type='internal' AND quantity_hours > 0).
 *   Samme filter som TaskApprovalSection.tsx på prosjektkortet.
 * - **Bilag**: `vouchers` med `status='pending_approval'` for prosjekter PL eier.
 * - **Fakturering**: prosjekter der ProjectHeader.tsx ville vist
 *   "Prosjektet er aktivt, men ingen faktura er opprettet ennå" — dvs.
 *   `project_status='active'` AND ingen invoice-rader (uavhengig av type
 *   og status). For completed-prosjekter samme regel for sluttfaktura.
 */
export function usePMInbox(userId: string | null) {
  return useQuery({
    queryKey: ["pm-inbox", userId],
    enabled: !!userId,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<PMInboxData> => {
      if (!userId) return { approvals: [], vouchers: [], invoices: [] };

      // 1. Prosjekter brukeren er PL på
      const { data: projects, error: projErr } = await supabase
        .from("projects")
        .select("id, name, project_code, project_status, total_amount, customer_id, startup_invoice_pct")
        .eq("production_lead_id", userId)
        .in("project_status", ["active", "kommende", "completed"])
        .is("deleted_at", null);
      if (projErr) throw projErr;

      const activeProjects = projects ?? [];
      if (activeProjects.length === 0) {
        return { approvals: [], vouchers: [], invoices: [] };
      }
      const projectIds = activeProjects.map((p) => p.id);

      // 2. Kundenavn separat så customers-RLS ikke maskerer prosjekter
      const customerIds = [
        ...new Set(activeProjects.map((p) => p.customer_id).filter(Boolean)),
      ] as string[];
      const customerMap = new Map<string, string>();
      if (customerIds.length > 0) {
        const { data: customers } = await supabase
          .from("customers")
          .select("id, name")
          .in("id", customerIds);
        for (const c of customers ?? []) {
          customerMap.set(c.id, c.name ?? "");
        }
      }

      const projectMap = new Map(
        activeProjects.map((p) => [
          p.id,
          {
            id: p.id,
            code: p.project_code ?? "",
            name: p.name ?? "",
            customerName: p.customer_id ? customerMap.get(p.customer_id) ?? null : null,
            status: p.project_status,
            totalAmount: Number(p.total_amount ?? 0),
            // Oppstartsandel fra `startup_invoice_pct`. Dette er det operative
            // feltet create-invoice fakturerer på, og det eneste som faktisk
            // holder 0 % i prod (invoice_terms[0].pct driftet på default 60 og
            // er aldri 0). Sluttandel = resten (100 − oppstart).
            startupPct: Number(p.startup_invoice_pct ?? 0),
          },
        ]),
      );

      // 3. Hent tre datasett parallelt — samme query-mønster som usePMProjects
      const [assignmentsRes, vouchersRes, invoicesRes] = await Promise.all([
        supabase
          .from("budget_line_assignments")
          .select(
            "id, hours, user_id, completed_at, budget_lines!inner(description, phase, project_id, cost_type, quantity_hours)",
          )
          .in("budget_lines.project_id", projectIds)
          .eq("task_status", "completed"),
        supabase
          .from("vouchers")
          .select(
            "id, project_id, voucher_no, supplier_name, description, amount, voucher_date, due_date, created_at",
          )
          .in("project_id", projectIds)
          // VIB-874: kanonisk status er 'pending_approval' (jf. db-types +
          // use-pm-projects). Legacy 'pending' brukes ikke for nye rader,
          // så det gamle filteret gjorde Bilag-seksjonen alltid tom.
          .eq("status", "pending_approval"),
        // Invoice-regel matcher ProjectHeader.tsx:231 — bare sjekk om
        // prosjektet har EN faktura overhodet (ingen status-filter,
        // ingen type-filter). Hvis ingen invoice = mangler.
        supabase.from("invoices").select("project_id").in("project_id", projectIds),
      ]);

      if (assignmentsRes.error) throw assignmentsRes.error;
      if (vouchersRes.error) throw vouchersRes.error;
      if (invoicesRes.error) throw invoicesRes.error;

      const assignments = assignmentsRes.data ?? [];
      const vouchers = vouchersRes.data ?? [];
      const invoicedProjectIds = new Set(
        (invoicesRes.data ?? [])
          .map((inv) => inv.project_id)
          .filter((id): id is string => !!id),
      );

      // 4. Hent brukernavn for tildelte
      const userIds = [...new Set(assignments.map((a) => a.user_id).filter(Boolean))] as string[];
      const userMap = new Map<string, { name: string; avatar: string | null }>();
      if (userIds.length > 0) {
        const { data: users } = await supabase
          .from("users")
          .select("id, full_name, avatar_url")
          .in("id", userIds);
        for (const u of users ?? []) {
          userMap.set(u.id, { name: u.full_name ?? "Ukjent", avatar: u.avatar_url ?? null });
        }
      }

      // 5. Bygg approvals — match isTaskLine-filter klient-side
      const approvals: InboxApproval[] = [];
      for (const a of assignments) {
        const bl = a.budget_lines as unknown as {
          description: string | null;
          phase: string | null;
          project_id: string;
          cost_type: string | null;
          quantity_hours: number | null;
        } | null;
        if (!bl) continue;
        if (bl.cost_type !== "internal" || (bl.quantity_hours ?? 0) <= 0) continue;
        const proj = projectMap.get(bl.project_id);
        if (!proj) continue;
        const fromUser = userMap.get(a.user_id) ?? { name: "Ukjent", avatar: null };
        approvals.push({
          id: a.id,
          projectId: proj.id,
          projectCode: proj.code,
          projectName: proj.name,
          customerName: proj.customerName,
          taskDescription: bl.description ?? "—",
          phase: bl.phase,
          hours: Number(a.hours ?? 0),
          fromUserId: a.user_id,
          fromUserName: fromUser.name,
          fromUserAvatar: fromUser.avatar,
          ageDays: daysSince(a.completed_at),
        });
      }

      // 6. Bygg vouchers
      const inboxVouchers: InboxVoucher[] = vouchers.map((v) => {
        const proj = projectMap.get(v.project_id ?? "");
        return {
          id: v.id,
          projectId: proj?.id ?? "",
          projectCode: proj?.code ?? "",
          projectName: proj?.name ?? "",
          customerName: proj?.customerName ?? null,
          voucherNumber: v.voucher_no != null ? String(v.voucher_no) : "",
          supplierName: v.supplier_name ?? null,
          description: v.description ?? "",
          amount: Number(v.amount ?? 0),
          voucherDate: v.voucher_date ?? null,
          dueDate: v.due_date ?? null,
          ageDays: daysSince(v.created_at),
        };
      });

      // 7. Bygg fakturering — matcher ProjectHeader.tsx-regelen:
      //    aktivt prosjekt uten EN ENESTE invoice = trenger oppstart.
      //    Completed prosjekt uten invoice = trenger slutt.
      const inboxInvoices: InboxInvoice[] = [];
      for (const proj of activeProjects) {
        if (invoicedProjectIds.has(proj.id)) continue;
        const projData = projectMap.get(proj.id)!;
        const startupPct = projData.startupPct;
        if (proj.project_status === "active" || proj.project_status === "kommende") {
          // Hopp over når oppstart er 0 % — da skal ingen oppstartsfaktura
          // sendes (jf. feedback prosjekt 541, oppstart 0 %).
          if (startupPct <= 0) continue;
          inboxInvoices.push({
            projectId: projData.id,
            projectCode: projData.code,
            projectName: projData.name,
            customerName: projData.customerName,
            invoiceType: "startup",
            pct: startupPct,
            estimatedAmount: projData.totalAmount * (startupPct / 100),
            ageDays: 0,
          });
        } else if (proj.project_status === "completed") {
          // Sluttfaktura = resten av kontraktssummen (100 − oppstart).
          const finalPct = 100 - startupPct;
          if (finalPct <= 0) continue;
          inboxInvoices.push({
            projectId: projData.id,
            projectCode: projData.code,
            projectName: projData.name,
            customerName: projData.customerName,
            invoiceType: "final",
            pct: finalPct,
            estimatedAmount: projData.totalAmount * (finalPct / 100),
            ageDays: 0,
          });
        }
      }

      return {
        approvals,
        vouchers: inboxVouchers,
        invoices: inboxInvoices,
      };
    },
  });
}
